/**
 * Perfis de operação (spec 017, D2): o vocabulário de um vertical é DADO, não
 * código. Medido em 2026-09-28 (`5c943de`): a marca "pooltrack" estava escrita
 * à mão em 6 telas e "placa" em busca, lista, novo ticket e OS — um segundo
 * cliente veria a marca e o jargão de outro.
 *
 * O tenant escolhe um preset (vocabulário, recurso principal, naturezas) e pode
 * sobrescrever termo a termo; `resolveTenantProfile` junta tudo num dicionário
 * COMPLETO, que é o que a API serve em /me, no portal e nas telas públicas. Este
 * arquivo é o ÚNICO lugar do SPA onde palavras de vertical podem existir — a
 * guarda de vocabulário (apps/api/src/shared/vertical-vocabulary.test.ts) o
 * exclui da varredura e reprova qualquer outro.
 *
 * Convenção dos termos: substantivos (ticket, client, contact, resource,
 * resourceId, desk, stage e plurais) ficam na forma do MEIO da frase ("placa",
 * "associação") e a tela usa `capitalize()` para rótulos; `situation.*` e
 * `nature.*` já são rótulos prontos ("Aguardando associação", "Instalação").
 */
import { z } from 'zod';
import { DESK_NATURES, type DeskNature } from './desks';
import { normalizeForCompare } from './sgr';
import { situationTermKey, type StageSituation, type TicketSituation } from './situations';

// ── Presets ─────────────────────────────────────────────────────────────────
export const PROFILE_PRESETS = ['vehicle_tracking', 'generic'] as const;
export const ProfilePresetSchema = z.enum(PROFILE_PRESETS);
export type ProfilePreset = z.infer<typeof ProfilePresetSchema>;

export const PROFILE_PRESET_LABELS: Record<ProfilePreset, string> = {
  vehicle_tracking: 'Rastreamento veicular',
  generic: 'Genérico',
};

/**
 * De onde vem o "recurso" de um atendimento:
 * - `ticket_identifier`: extraído do próprio ticket — a placa que a busca da
 *   spec 012 tira da ficha/título (`search_plate`);
 * - `equipment`: o equipamento cadastrado em Recursos e ligado ao ticket;
 * - `none`: o tenant não trabalha com recurso — a coluna e o filtro somem.
 */
export const RESOURCE_SOURCES = ['ticket_identifier', 'equipment', 'none'] as const;
export const ResourceSourceSchema = z.enum(RESOURCE_SOURCES);
export type ResourceSource = z.infer<typeof ResourceSourceSchema>;

export const RESOURCE_SOURCE_LABELS: Record<ResourceSource, string> = {
  ticket_identifier: 'Identificador extraído do atendimento',
  equipment: 'Equipamento cadastrado em Recursos',
  none: 'Sem recurso',
};

/** De onde vem o recurso de um tenant: o do perfil gravado, senão o do preset. */
export function profileResourceSource(profile: Pick<TenantProfile, 'preset' | 'resource'>): ResourceSource {
  return profile.resource?.source ?? PROFILE_PRESET_DEFINITIONS[profile.preset].resourceSource;
}

/**
 * O rótulo do recurso de UM ticket (spec 017, D2): a placa extraída da ficha
 * (`ticket_identifier`), o equipamento ligado (`equipment`) ou nada (`none`).
 * Regra única — a lista de tickets (`resourceLabel`) e a agenda de placas
 * (`ticketPlate`, spec 039) chamam esta.
 */
export function ticketResourceLabel(
  source: ResourceSource,
  row: { searchPlate: string | null; equipmentName: string | null }
): string | null {
  const label =
    source === 'ticket_identifier' ? row.searchPlate : source === 'equipment' ? row.equipmentName : null;
  return label ?? null;
}

// ── Termos ──────────────────────────────────────────────────────────────────
export const TERM_KEYS = [
  'ticket',
  'tickets',
  'client',
  'clients',
  'contact',
  'contacts',
  'resource',
  'resources',
  'resourceId',
  'desk',
  'desks',
  'stage',
  'stages',
  // uma por situação (TICKET_SITUATIONS) — o teste confere que não falta nenhuma
  'situation.new',
  'situation.in_progress',
  'situation.waiting_client',
  'situation.waiting_contact',
  'situation.waiting_third_party',
  'situation.done',
  'situation.canceled',
  // uma por natureza de mesa (DESK_NATURES) — idem
  'nature.installation',
  'nature.maintenance',
  'nature.removal',
  'nature.support',
  'nature.other',
  'nature.internal',
] as const;
export const TermKeySchema = z.enum(TERM_KEYS);
export type TermKey = z.infer<typeof TermKeySchema>;
export type Terms = Record<TermKey, string>;

export const natureTermKey = (n: DeskNature): `nature.${DeskNature}` => `nature.${n}`;

/**
 * Situações cujo rótulo DERIVA de outro termo quando o admin não o sobrescreve:
 * "Aguardando <cliente>" e "Aguardando <contato>". Trocar "associação" por
 * "cooperativa" troca "Aguardando cooperativa" junto, em todas as telas.
 */
const DERIVED: Partial<Record<TermKey, (t: Terms) => string>> = {
  'situation.waiting_client': (t) => `Aguardando ${t.client}`,
  'situation.waiting_contact': (t) => `Aguardando ${t.contact}`,
};

type PresetTerms = Omit<Terms, 'situation.waiting_client' | 'situation.waiting_contact'>;

const COMMON_TERMS = {
  ticket: 'atendimento',
  tickets: 'atendimentos',
  desk: 'mesa',
  desks: 'mesas',
  stage: 'etapa',
  stages: 'etapas',
  'situation.new': 'Novo',
  'situation.in_progress': 'Em andamento',
  'situation.waiting_third_party': 'Aguardando terceiro',
  'situation.done': 'Concluído',
  'situation.canceled': 'Cancelado',
  'nature.installation': 'Instalação',
  'nature.maintenance': 'Manutenção',
  'nature.removal': 'Retirada',
  'nature.support': 'Suporte',
  'nature.internal': 'Interna',
} as const;

interface PresetDefinition {
  terms: PresetTerms;
  resourceSource: ResourceSource;
  /**
   * Naturezas com que o perfil trabalha, na ordem dos indicadores do portal
   * (019). `internal` nunca entra: é justamente a que o cliente não vê. A
   * configuração da mesa oferece TODAS — isto é o que aparece para o cliente.
   */
  natures: readonly DeskNature[];
}

export const PROFILE_PRESET_DEFINITIONS: Record<ProfilePreset, PresetDefinition> = {
  // PoolTrack: a associação é o cliente B2B; o associado, dono do veículo, é o
  // contato; o recurso é o veículo, identificado pela placa da ficha do SGR.
  vehicle_tracking: {
    terms: {
      ...COMMON_TERMS,
      client: 'associação',
      clients: 'associações',
      contact: 'associado',
      contacts: 'associados',
      resource: 'veículo',
      resources: 'veículos',
      resourceId: 'placa',
      'nature.other': 'Outro',
    },
    resourceSource: 'ticket_identifier',
    natures: ['installation', 'maintenance', 'removal', 'support', 'other'],
  },
  generic: {
    terms: {
      ...COMMON_TERMS,
      client: 'cliente',
      clients: 'clientes',
      contact: 'contato',
      contacts: 'contatos',
      resource: 'recurso',
      resources: 'recursos',
      resourceId: 'identificador',
      'nature.other': 'Solicitação',
    },
    resourceSource: 'equipment',
    natures: ['support', 'other'],
  },
};

// ── Perfil gravado (org_settings.profile) ───────────────────────────────────
const TermValue = z.string().trim().min(1).max(60);

/** Só as chaves conhecidas: termo desconhecido é 422, nunca gravado calado. */
export const TermOverridesSchema = z.partialRecord(TermKeySchema, TermValue);
export type TermOverrides = z.infer<typeof TermOverridesSchema>;

export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, '#RRGGBB');

/**
 * Contrato ÚNICO do perfil do tenant (FR-004) — o que fica em
 * `org_settings.profile` e o que o PUT /org/profile aceita. Troca de preset NÃO
 * apaga `terms`: o sobrescrito continua valendo por cima do preset novo.
 */
export const TenantProfileSchema = z
  .object({
    preset: ProfilePresetSchema,
    terms: TermOverridesSchema.optional(),
    resource: z.object({ source: ResourceSourceSchema }).strict().optional(),
    brand: z.object({ primaryColor: HexColorSchema.optional() }).strict().optional(),
  })
  .strict();
export type TenantProfile = z.infer<typeof TenantProfileSchema>;

export const DEFAULT_TENANT_PROFILE: TenantProfile = { preset: 'generic' };

/**
 * jsonb do banco → perfil válido. Dado inválido (gravado por fora do contrato)
 * vira o padrão em vez de derrubar /me — a tela perde o vocabulário do tenant,
 * mas abre; o chamador registra o motivo.
 */
export function parseStoredTenantProfile(raw: unknown): { profile: TenantProfile; valid: boolean } {
  const parsed = TenantProfileSchema.safeParse(raw);
  return parsed.success
    ? { profile: parsed.data, valid: true }
    : { profile: DEFAULT_TENANT_PROFILE, valid: false };
}

// ── Marca ───────────────────────────────────────────────────────────────────
/** Marca do PRODUTO: aparece quando não há tenant (login) ou o tenant não tem nome. */
export const PRODUCT_BRAND_NAME = 'FatorH ERP';

export const BrandSchema = z.object({
  /** nome fantasia da organização → nome do tenant → nome do produto */
  name: z.string(),
  /**
   * Autenticado: CAMINHO da rota de serving (`/brand/logo`, 302/modo JSON —
   * cliente e equipe). Público: URL presignada de curta duração. null = sem logo.
   */
  logoUrl: z.string().nullable(),
  primaryColor: HexColorSchema.nullable(),
});
export type Brand = z.infer<typeof BrandSchema>;

export const PRODUCT_BRAND: Brand = { name: PRODUCT_BRAND_NAME, logoUrl: null, primaryColor: null };

// ── Perfil resolvido (o que as telas consomem) ──────────────────────────────
export const ResolvedTenantProfileSchema = z.object({
  preset: ProfilePresetSchema,
  terms: z.record(TermKeySchema, z.string()),
  resource: z.object({
    source: ResourceSourceSchema,
    /** rótulo do recurso ("veículo"), forma do meio da frase */
    label: z.string(),
    /** rótulo do identificador ("placa"), forma do meio da frase */
    idLabel: z.string(),
  }),
  natures: z.array(z.enum(DESK_NATURES)),
  brand: BrandSchema,
});
export type ResolvedTenantProfile = z.infer<typeof ResolvedTenantProfileSchema>;

/** Preset + sobrescritas + derivados → dicionário completo, sem buracos. */
export function resolveTerms(profile: Pick<TenantProfile, 'preset' | 'terms'>): Terms {
  const base = PROFILE_PRESET_DEFINITIONS[profile.preset].terms;
  const merged = { ...base, ...(profile.terms ?? {}) } as Terms;
  for (const key of TERM_KEYS) {
    if (profile.terms?.[key] !== undefined) continue;
    const derive = DERIVED[key];
    if (derive) merged[key] = derive(merged);
  }
  return merged;
}

export function resolveTenantProfile(
  profile: TenantProfile,
  brand: { name: string | null | undefined; logoUrl: string | null }
): ResolvedTenantProfile {
  const definition = PROFILE_PRESET_DEFINITIONS[profile.preset];
  const terms = resolveTerms(profile);
  return {
    preset: profile.preset,
    terms,
    resource: {
      source: profile.resource?.source ?? definition.resourceSource,
      label: terms.resource,
      idLabel: terms.resourceId,
    },
    natures: [...definition.natures],
    brand: {
      name: brand.name?.trim() || PRODUCT_BRAND_NAME,
      logoUrl: brand.logoUrl,
      primaryColor: profile.brand?.primaryColor ?? null,
    },
  };
}

// ── Helpers de texto ────────────────────────────────────────────────────────
/** "placa" → "Placa"; só a primeira letra — "IMEI" continua "IMEI". */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * O termo do cliente é do tenant ("associação" no perfil de rastreamento,
 * "cliente" no genérico, ou o que o admin escreveu) e não carrega gênero. O
 * artigo sai da terminação do singular: -a, -ção/-são, -dade e -gem são
 * femininos ("associação", "cooperativa", "empresa", "unidade"); o resto,
 * masculino ("cliente", "condomínio", "parceiro").
 *
 * Mora aqui (e não no web) desde o extrato em PDF: o cabeçalho do documento
 * diz "Todas as associações" com a MESMA regra do seletor da tela.
 */
export function isFeminineTerm(singular: string): boolean {
  const w = normalizeForCompare(singular).toLowerCase().split(' ')[0] ?? '';
  return /(a|cao|sao|dade|gem)$/.test(w);
}

/** "Todas as associações" / "Todos os clientes" — o filtro de cliente vazio */
export function allClientsLabel(terms: Pick<Terms, 'client' | 'clients'>): string {
  return isFeminineTerm(terms.client) ? `Todas as ${terms.clients}` : `Todos os ${terms.clients}`;
}

/** Rótulo da situação no vocabulário do tenant. */
export function situationLabel(terms: Terms, situation: TicketSituation): string {
  return terms[situationTermKey(situation)];
}

/** Rótulo da natureza no vocabulário do tenant. */
export function natureLabel(terms: Terms, nature: DeskNature): string {
  return terms[natureTermKey(nature)];
}

// ── Sugestão de situação pelo nome da etapa (spec 017, FR-002) ─────────────────────────────────────────────
/**
 * Nome da etapa pronto para comparar: minúsculo, sem acento, espaços colapsados.
 *
 * GÊMEA de `btrim(regexp_replace(erp.norm_text(name), '\s+', ' ', 'g'))`, que a
 * migration da spec 017 usa para preencher as etapas que já existem. Mudar uma
 * regra aqui sem mudar a outra é ter a tela sugerindo uma coisa e o backfill
 * gravado outra — o teste do backfill roda os dois sobre os nomes reais.
 */
export function foldStageName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** "4.2-", "1 - ", "7.1 - ", "2.1 -": a numeração do funil que a PoolTrack usa. */
const NUMBERING = /^\d+(?:\.\d+)*\s*-?\s*/;

/**
 * O que "cliente" quer dizer no nome de uma etapa depende do vertical. Na
 * PoolTrack, "cliente" é o ASSOCIADO — quem tem o veículo — e o cliente B2B é
 * a associação: "4.2-Aguardando retorno do cliente" espera o associado. Num
 * tenant genérico, cliente é o cliente. É a única palavra das regras abaixo cujo
 * sentido muda com o perfil.
 */
const CLIENT_WORD_MEANS: Record<ProfilePreset, 'waiting_client' | 'waiting_contact'> = {
  vehicle_tracking: 'waiting_contact',
  generic: 'waiting_client',
};

/**
 * Situação sugerida pelo NOME de uma etapa intermediária, ou `null` quando o
 * nome não diz com clareza — aí a etapa fica "Em andamento" e vai para a lista
 * "a revisar" (edge case da spec). Primeira regra que casa vence:
 *
 * 1. frases que dizem de quem é a vez: dado/contato inválido (quem corrige é o
 *    cliente — a associação), "disparo realizado" (a mensagem foi, espera-se o
 *    contato, spec 014), "veículo indisponível" (o associado precisa liberar);
 * 2. trabalho em curso da equipe — "Em …", tratativa, andamento, análise,
 *    acompanhamento, "contato com" — desde que o nome não diga "aguardando";
 * 3. etapa intermediária que já é o fim (concluído, finalizado, resolvido,
 *    encerrado — o particípio);
 * 4. de quem se espera: associação → cliente; técnico, prestador, correios,
 *    equipamento, fornecedor, transportadora, terceiro → terceiro; associado,
 *    contato, solicitante → contato; e "cliente" conforme o perfil.
 *
 * Nunca sugere `new`: "4-Pendente financeiro" não é etapa inicial. Só a
 * sentinela `is_first` nasce Novo (ver initialStageSituation).
 */
export function suggestStageSituation(name: string, preset: ProfilePreset): StageSituation | null {
  const n = foldStageName(name);
  const core = n.replace(NUMBERING, '');

  if (/invalid/.test(n)) return 'waiting_client';
  if (/disparo realizado/.test(n)) return 'waiting_contact';
  if (/veiculo indisponivel/.test(n)) return 'waiting_contact';

  const waiting = /aguard/.test(n);
  if (!waiting) {
    if (/^em /.test(core) || /tratativa|andamento|analise|acompanhamento|contato com/.test(n)) {
      return 'in_progress';
    }
    // particípio, não verbo: "Finalizar cadastro" é trabalho a fazer
    if (/concluid|finalizad|resolvid|encerrad/.test(n)) return 'done';
  }

  if (/associac/.test(n)) return 'waiting_client';
  if (/tecnic|prestador|correio|equipamento|fornecedor|transportadora|terceiro/.test(n)) {
    return 'waiting_third_party';
  }
  if (/associad|contato|solicitante/.test(n)) return 'waiting_contact';
  if (/cliente/.test(n)) return CLIENT_WORD_MEANS[preset];
  return null;
}

/**
 * Situação com que uma etapa NASCE (criada no ERP, trazida pelo espelho do
 * TiFlux ou preenchida pela migration): `is_first` → Novo, `is_last` →
 * Concluído, senão a sugestão pelo nome. `reviewed: false` marca "a revisar":
 * só o que caiu no padrão Em andamento por falta de sugestão (FR-002) — a
 * etapa sai da lista quando alguém a salva.
 */
export function initialStageSituation(
  stage: { name: string; isFirst: boolean; isLast: boolean },
  preset: ProfilePreset
): { situation: StageSituation; reviewed: boolean } {
  if (stage.isFirst) return { situation: 'new', reviewed: true };
  if (stage.isLast) return { situation: 'done', reviewed: true };
  const suggested = suggestStageSituation(stage.name, preset);
  return suggested
    ? { situation: suggested, reviewed: true }
    : { situation: 'in_progress', reviewed: false };
}

// ── Sugestão de natureza pelo nome da mesa (spec 017, FR-003) ───────────────

/**
 * Mesas que são assunto da operação e não do cliente, pelo nome exato (sem
 * acento e sem caixa). A lista do plano da 017 — "Busca de Prestador" fica de
 * fora de propósito: a associação participa dela ("2-Aguardando retorno da
 * associação"). Tudo revisável na tela da mesa.
 */
const INTERNAL_DESK_NAMES: ReadonlySet<string> = new Set([
  'PAGAMENTO DE PRESTADOR',
  'FINANCEIRO',
  'ESTOQUE',
  'SETOR TESTE',
]);

/**
 * Natureza SUGERIDA pelo nome da mesa: a heurística que a OS usava (016) mais a
 * lista de internas. Serve à migration da 017 (gêmea em SQL), ao espelho do
 * TiFlux e à criação de mesa sem natureza informada — nunca mais à OS, que lê a
 * natureza gravada. Na conta real: "Instalar" (exibida "Instalação de
 * Rastreador"), "Manutenção" ("Tickets de Manutencao", sem acento), "Retirar"
 * ("Retirada do Rastreador"). "Implantação" e "Reativação" NÃO são instalação:
 * por isso o radical é INSTAL, não "…AÇÃO". O resto é suporte.
 */
export function suggestDeskNature(...names: Array<string | null | undefined>): DeskNature {
  for (const raw of names) {
    if (raw && INTERNAL_DESK_NAMES.has(normalizeForCompare(raw))) return 'internal';
  }
  for (const raw of names) {
    if (!raw) continue;
    const name = normalizeForCompare(raw);
    if (name.includes('INSTAL')) return 'installation';
    if (name.includes('MANUTEN')) return 'maintenance';
    if (name.includes('RETIR')) return 'removal';
  }
  return 'support';
}
